class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._isOpen = false; // Internal state flag

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink)
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  }

  open(triggeredBy) {
    // Prevent opening if already open
    if (this._isOpen) return;

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Set state and class immediately (synchronously) before setTimeout
    this._isOpen = true;
    this.classList.add('active');
    
    // Attach quantity handlers when drawer opens
    setTimeout(() => {
      this.attachQuantityHandlers();
    }, 50);
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate');
    }, 0);

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      // Check if trapFocus is available before using it
      if (typeof trapFocus === 'function' && containerToTrapFocusOn) {
        trapFocus(containerToTrapFocusOn, focusElement);
      } else if (focusElement) {
        // Fallback: just focus the element if trapFocus is not available
        focusElement.focus();
      }
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Prevent closing if already closed
    if (!this._isOpen) return;

    // Set closed state immediately (synchronously)
    this._isOpen = false;
    this.classList.remove('active', 'animate');
    // Check if removeTrapFocus is available before using it
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    } else if (this.activeElement) {
      // Fallback: just focus the active element if removeTrapFocus is not available
      this.activeElement.focus();
    }
    
    // Always ensure overflow-hidden is removed from body
    document.body.classList.remove('overflow-hidden');
    
    // Fallback: Force remove overflow-hidden after a short delay to handle edge cases
    setTimeout(() => {
      if (document.body.classList.contains('overflow-hidden')) {
        document.body.classList.remove('overflow-hidden');
      }
    }, 100);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    // Use inline function to handle escape key instead of relying on global onKeyUpEscape
    cartDrawerNote.parentElement.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() !== 'ESCAPE') return;

      const openDetailsElement = event.target.closest('details[open]');
      if (!openDetailsElement) return;

      const summaryElement = openDetailsElement.querySelector('summary');
      openDetailsElement.removeAttribute('open');
      if (summaryElement) {
        summaryElement.setAttribute('aria-expanded', false);
        summaryElement.focus();
      }
    });
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    // Update cart count in header after sections are rendered
    setTimeout(() => {
      this.updateCartCountFromDOM();
    }, 50);

    // Attach quantity button handlers directly after render
    this.attachQuantityHandlers();

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      // Only open if not already open
      if (!this._isOpen) {
        this.open();
      }
    });
  }

  attachQuantityHandlers() {
    // Attach event listeners directly to quantity buttons in cart drawer
    const quantityButtons = this.querySelectorAll('.quantity__button:not([data-handler-attached])');
    
    quantityButtons.forEach((button) => {
      // Mark as attached to avoid duplicates
      button.setAttribute('data-handler-attached', 'true');
      
      // Attach click handler
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        const quantityInput = button.closest('quantity-input');
        if (!quantityInput) return;
        
        const input = quantityInput.querySelector('input[name="updates[]"]');
        if (!input) return;
        
        const previousValue = parseInt(input.value) || 0;
        const isPlus = button.name === 'plus';
        const newValue = isPlus ? previousValue + 1 : Math.max(0, previousValue - 1);
        
        input.value = newValue;
        
        // Trigger change event to update cart
        const changeEvent = new Event('change', { bubbles: true });
        input.dispatchEvent(changeEvent);
      });
    });
  }

  updateCartCountFromDOM() {
    // Get cart count from cart-icon-bubble section that was just rendered
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble) {
      const countBubble = cartIconBubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (countBubble) {
        const itemCount = parseInt(countBubble.textContent) || 0;
        this.updateCartCount(itemCount);
      } else {
        // If no count bubble, cart might be empty
        this.updateCartCount(0);
      }
    } else {
      // Fallback: count items in cart drawer
      const cartItems = this.querySelectorAll('.cart-item');
      this.updateCartCount(cartItems.length);
    }
  }

  updateCartCount(itemCount) {
    // Update cart count in header (.header-new__cart-count)
    const cartCountElements = document.querySelectorAll('.header-new__cart-count');
    cartCountElements.forEach((element) => {
      if (itemCount > 0) {
        element.textContent = itemCount;
        // Show element if it was hidden
        if (element.style.display === 'none') {
          element.style.display = '';
        }
      } else {
        // Hide element if cart is empty
        element.style.display = 'none';
      }
    });

    // Also update cart-count-bubble if it exists (for other parts of the site)
    const cartCountBubbles = document.querySelectorAll('.cart-count-bubble');
    cartCountBubbles.forEach((bubble) => {
      const countSpan = bubble.querySelector('span[aria-hidden="true"]');
      if (countSpan) {
        if (itemCount > 0 && itemCount < 100) {
          countSpan.textContent = itemCount;
          bubble.style.display = '';
        } else if (itemCount >= 100) {
          countSpan.textContent = '99+';
          bubble.style.display = '';
        } else {
          bubble.style.display = 'none';
        }
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

